import { z } from 'zod';
import { resolvedCheckInFieldSchema } from './check-in-flow.js';
import { localeCodeSchema, multiLanguageContentSchema } from './i18n.js';
import { INVITE_CODE_PATTERN, inviteDTOSchema, invitePreRegistrationSummarySchema, preRegistrationAnswerValueSchema, storedPreRegistrationAnswerValueSchema, } from './invite.js';
import { ACCEPTANCE_TYPED_NAME_MAX_CHARS } from './visit.js';
/**
 * S41 — the Visit Pass (execution-plan/milestones/S41-guest-pre-registration.md).
 *
 * Two audiences, one file:
 *   - the ADMIN detail read (`GET /api/web/invites/:id`), and
 *   - the PUBLIC pass surface (`/api/public/v1/visit-pass*`), authenticated by the pass token
 *     in the `X-Visit-Pass` header and nothing else.
 *
 * The public DTO is MINIMAL DISCLOSURE by construction (spec "Security design"): no host email
 * or phone, no ids beyond what the page needs to post back (`documentId`, `versionId`,
 * `visitorTypeId`, field keys), no other invite's data.
 */
/** The request header the Visit Pass SPA sends the fragment token in. Lower-case, as Node sees it. */
export const VISIT_PASS_HEADER = 'x-visit-pass';
/** 32 CSPRNG bytes, base64url, unpadded — always 43 characters. */
export const VISIT_PASS_TOKEN_PATTERN = /^[A-Za-z0-9_-]{43}$/;
/* -------------------------------------------------------------------------- */
/* Admin — invite detail                                                       */
/* -------------------------------------------------------------------------- */
export const invitePreRegisteredAnswerSchema = z.object({
    fieldKey: z.string().min(1),
    /** The field's label from the invite location's CURRENT flow; `null` when the field is gone. */
    label: multiLanguageContentSchema.nullable(),
    /** OUTBOUND: a stored answer, read back as stored — never re-validated (handoff 0009 §1a rule 12). */
    value: storedPreRegistrationAnswerValueSchema,
});
export const invitePreRegisteredSignatureSchema = z.object({
    documentId: z.string().min(1),
    documentVersionId: z.string().min(1),
    versionNumber: z.number().int().positive(),
    renderedTitle: z.string().nullable(),
    typedName: z.string().nullable(),
    hasDrawnSignature: z.boolean(),
    signedAt: z.string().datetime({ offset: true }),
});
/** `GET /api/web/invites/:id` — the list row plus what the visitor pre-registered. */
export const inviteDetailDTOSchema = inviteDTOSchema.extend({
    /**
     * The type the VISITOR chose on their pass, `null` when they have not. Distinct from the
     * inherited `visitorTypeId`, which is the one the admin chose when issuing the invite.
     */
    preRegisteredVisitorTypeId: z.string().nullable(),
    answers: z.array(invitePreRegisteredAnswerSchema),
    signatures: z.array(invitePreRegisteredSignatureSchema),
});
/* -------------------------------------------------------------------------- */
/* Public — the Visit Pass                                                     */
/* -------------------------------------------------------------------------- */
/**
 * `ACTIVE` — writable. `CHECKED_IN` — the invite was redeemed; the pass is read-only
 * ("You're checked in"). Expired and cancelled passes never get this DTO: they are a `410`
 * carrying only `{ status }` (see `visitPassGoneDetailsSchema`).
 */
export const VISIT_PASS_STATES = ['ACTIVE', 'CHECKED_IN'];
export const visitPassStateSchema = z.enum(VISIT_PASS_STATES);
/** The `details` of a `410 VISIT_PASS_GONE` — a status and nothing else. */
export const VISIT_PASS_GONE_STATUSES = ['CANCELLED', 'EXPIRED'];
export const visitPassGoneDetailsSchema = z.object({ status: z.enum(VISIT_PASS_GONE_STATUSES) });
/** One FIELD_COLLECTION screen as the pass renders it — invite-fixed fields already removed. */
export const visitPassQuestionScreenSchema = z.object({
    screenId: z.string().min(1),
    title: multiLanguageContentSchema.nullable(),
    body: multiLanguageContentSchema.nullable(),
    fields: z.array(resolvedCheckInFieldSchema),
});
export const visitPassVisitorTypeSchema = z.object({
    id: z.string().min(1),
    label: multiLanguageContentSchema,
});
export const visitPassAgreementSchema = z.object({
    documentId: z.string().min(1),
    versionId: z.string().min(1),
    versionNumber: z.number().int().positive(),
    /** Rendered in the flow's default locale with this visit's values substituted. */
    renderedTitle: z.string().nullable(),
    renderedBody: z.string().nullable(),
    /** `true` when the document's source is a PDF (the body is then a summary, not the text). */
    hasSourcePdf: z.boolean(),
    requiresTypedName: z.boolean(),
    requiresDrawnSignature: z.boolean(),
    /** Signed for THIS version ahead of arrival. A signature of an older version reads `false`. */
    signed: z.boolean(),
    signedAt: z.string().datetime({ offset: true }).nullable(),
});
/** Everything a calendar file needs; the SPA builds the `.ics` from it. */
export const visitPassCalendarSchema = z.object({
    uid: z.string().min(1),
    title: z.string().min(1),
    start: z.string().datetime({ offset: true }),
    end: z.string().datetime({ offset: true }),
    location: z.string().nullable(),
    description: z.string().nullable(),
});
export const visitPassDTOSchema = z.object({
    state: visitPassStateSchema,
    tenant: z.object({ name: z.string().min(1) }),
    visitor: z.object({
        firstName: z.string().min(1),
        lastName: z.string().min(1),
        /** Masked (`a***@example.com`) — the pass never echoes the full address. */
        maskedEmail: z.string().min(1),
    }),
    host: z.object({ displayName: z.string().min(1) }),
    location: z.object({
        name: z.string().min(1),
        address: z.string().nullable(),
        timezone: z.string().min(1),
    }),
    expectedFrom: z.string().datetime({ offset: true }),
    expectedTo: z.string().datetime({ offset: true }),
    purpose: z.string().nullable(),
    /** The kiosk arrival code, DERIVED from the token (never stored in plaintext). */
    checkInCode: z.string().regex(INVITE_CODE_PATTERN),
    defaultLocale: localeCodeSchema,
    preRegistration: invitePreRegistrationSummarySchema,
    /** Field keys and document ids still required before the pass reads Ready. */
    missing: z.object({
        fieldKeys: z.array(z.string()),
        documentIds: z.array(z.string()),
        visitorType: z.boolean(),
    }),
    /** Empty when the location offers no choice. */
    visitorTypes: z.array(visitPassVisitorTypeSchema),
    visitorTypeId: z.string().nullable(),
    questions: z.array(visitPassQuestionScreenSchema),
    /** OUTBOUND: stored answers, never re-validated on read (handoff 0009 §1a rule 12). */
    answers: z.record(z.string(), storedPreRegistrationAnswerValueSchema),
    agreements: z.array(visitPassAgreementSchema),
    /** The flow takes a live photo at the kiosk — the pass says so; it never uploads one. */
    photoAtArrival: z.boolean(),
    calendar: visitPassCalendarSchema,
});
/**
 * `PUT /api/public/v1/visit-pass/answers` — autosave. PARTIAL: keys present are set, a `null`
 * value clears that answer, keys absent are left alone. `visitorTypeId` absent leaves the
 * choice alone; `null` clears it.
 */
export const saveVisitPassAnswersSchema = z
    .object({
    answers: z.record(z.string().min(1).max(64), preRegistrationAnswerValueSchema.nullable()).default({}),
    visitorTypeId: z.string().min(1).max(64).nullable().optional(),
})
    .refine((body) => Object.keys(body.answers).length <= 100, { message: 'TOO_MANY_ANSWERS', path: ['answers'] });
/** `POST /api/public/v1/visit-pass/agreements/:documentId/sign`. */
export const signVisitPassAgreementSchema = z.object({
    /** The version the visitor read — refused when it is no longer current. */
    documentVersionId: z.string().min(1).max(64),
    typedName: z.string().trim().min(1).max(ACCEPTANCE_TYPED_NAME_MAX_CHARS).optional(),
    /** From `POST /visit-pass/signatures`. */
    signatureFileId: z.string().min(1).max(64).optional(),
});
/** `POST /api/public/v1/visit-pass/signatures` → `{ fileId }`. */
export const visitPassSignatureUploadDTOSchema = z.object({ fileId: z.string().min(1) });
//# sourceMappingURL=visit-pass.js.map